import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle, Mail, ArrowLeft, CheckCircle } from 'lucide-react'
import axios from 'axios'

// Brand colors
const BRAND_NAVY = '#1e3a5f'
const BRAND_PINK = '#ff4081'

// Small logo for the form panel - bigger size
const SmallLogo = () => (
  <div className="flex items-center justify-center p-4 rounded-xl border border-gray-200 bg-white shadow-sm w-20 h-20">
    <svg viewBox="0 0 60 60" className="h-12 w-12" fill="none">
      <path d="M30 8 L52 20 L30 32 L8 20 Z" fill="#1e3a5f" />
      <path d="M30 16 L52 28 L30 40 L8 28 Z" fill="#7cb9e8" />
      <path d="M30 24 L52 36 L30 48 L8 36 Z" fill="#ff4081" />
      <path d="M30 32 L52 44 L30 56 L8 44 Z" fill="#4a90c2" />
    </svg>
  </div>
)

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      await axios.post('/api/auth/forgot-password', { email })
      setIsSubmitted(true)
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string } } }
        setError(axiosError.response?.data?.message || axiosError.response?.data?.error || 'Failed to send reset email. Please try again.')
      } else {
        setError('Failed to send reset email. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen flex">
        {/* Left Panel - Branding */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
          {/* Grid pattern overlay */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center p-12 w-full h-full">
            {/* Main tagline */}
            <div>
              <p className="text-sm font-semibold tracking-widest mb-4" style={{ color: BRAND_PINK }}>
                BLUEPRINT DESK
              </p>
              <h1 className="text-4xl lg:text-5xl font-light text-white leading-tight">
                The <em className="italic" style={{ color: '#94a3b8' }}>operating<br />system</em> for<br />
                <span className="font-semibold">high-growth<br />agencies.</span>
              </h1>
              <div className="w-16 h-1 mt-8" style={{ backgroundColor: BRAND_PINK }} />
            </div>
          </div>
        </div>

        {/* Right Panel - Success Content */}
        <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-8 bg-white">
          <div className="w-full max-w-md">
            {/* Small logo */}
            <div className="flex justify-center mb-12">
              <SmallLogo />
            </div>

            {/* Success Card */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full bg-green-100">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Check Your Email</h1>
              <p className="text-gray-500 mt-2">
                If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
              </p>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-500 text-center">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <Button
                variant="outline"
                className="w-full h-12 rounded-lg"
                onClick={() => setIsSubmitted(false)}
              >
                Try Again
              </Button>
              <Link to="/login" className="block">
                <Button
                  variant="outline"
                  className="w-full h-12 rounded-lg"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Form state
  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12 w-full h-full">
          {/* Main tagline */}
          <div>
            <p className="text-sm font-semibold tracking-widest mb-4" style={{ color: BRAND_PINK }}>
              BLUEPRINT DESK
            </p>
            <h1 className="text-4xl lg:text-5xl font-light text-white leading-tight">
              The <em className="italic" style={{ color: '#94a3b8' }}>operating<br />system</em> for<br />
              <span className="font-semibold">high-growth<br />agencies.</span>
            </h1>
            <div className="w-16 h-1 mt-8" style={{ backgroundColor: BRAND_PINK }} />
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Small logo */}
          <div className="flex justify-center mb-12">
            <SmallLogo />
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(30, 58, 95, 0.1)' }}>
                <Mail className="h-8 w-8" style={{ color: BRAND_NAVY }} />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 text-center">Forgot Password?</h1>
            <p className="text-gray-500 mt-2 text-center">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Email field */}
            <div>
              <Input
                id="email"
                type="email"
                placeholder="Work Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="email"
                className="h-12 bg-white border border-gray-300 rounded-lg px-4 focus:ring-0 focus:border-gray-400"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-white rounded-lg font-medium hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)' }}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>

            <Link to="/login" className="block">
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 rounded-lg"
                disabled={isLoading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </Link>
          </form>
        </div>
      </div>
    </div>
  )
}
